/** Workflow lifecycle for insurance policies (not evidence items). */
export type PolicyStatus =
  | "draft"
  | "queued_for_analysis"
  | "analyzing"
  | "awaiting_review"
  | "approved"
  | "rejected_by_ai"
  | "rejected_by_human"
  | "failed";

export const POLICY_ANALYZING_STATUSES = [
  "queued_for_analysis",
  "analyzing",
] as const;

export const POLICY_STATUS_LABELS: Record<PolicyStatus, string> = {
  draft: "Kladde",
  queued_for_analysis: "Analyseres",
  analyzing: "Analyseres",
  awaiting_review: "Afventer sekretariat",
  approved: "Godkendt",
  rejected_by_ai: "Afvist",
  rejected_by_human: "Afvist",
  failed: "Fejlede",
};

export const normalizePolicyStatus = (
  status: unknown,
): PolicyStatus | undefined => {
  if (typeof status !== "string" || status.trim().length === 0) {
    return undefined;
  }

  const normalizedStatus = status
    .trim()
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .replace(/-/g, "_")
    .toLowerCase();

  if (normalizedStatus in POLICY_STATUS_LABELS) {
    return normalizedStatus as PolicyStatus;
  }

  return undefined;
};

export const getPolicyStatusLabel = (
  status: PolicyStatus | string | null | undefined,
): string => {
  const normalizedStatus = normalizePolicyStatus(status);

  if (normalizedStatus) {
    return POLICY_STATUS_LABELS[normalizedStatus];
  }

  if (typeof status === "string" && status.trim().length > 0) {
    return status;
  }

  return "Ukendt";
};

export const isPolicyAnalyzingStatus = (
  status: PolicyStatus | string,
): boolean =>
  POLICY_ANALYZING_STATUSES.includes(
    status as (typeof POLICY_ANALYZING_STATUSES)[number],
  );

export const isPolicyApprovedStatus = (status: PolicyStatus): boolean =>
  status === "approved";

export const isPolicyRejectedStatus = (status: PolicyStatus): boolean =>
  status === "rejected_by_ai" || status === "rejected_by_human";

export const isPolicyRejectedByAiStatus = (status: PolicyStatus): boolean =>
  status === "rejected_by_ai";

export const isPolicyAwaitingReviewStatus = (status: PolicyStatus): boolean =>
  status === "awaiting_review";
